import { useCallback, useEffect, useState } from 'react';
import {
  archiveSegment,
  createSegment,
  listSegments,
  updateSegment,
  previewSegment,
  refreshSegment,
  segmentMembers,
  type MembersPage,
  type SegmentSummary,
} from '../api';
import { type IdentityLocale, type IdentityTranslate } from '../i18n';
import {
  Button,
  Card,
  DataTable,
  Field,
  Id,
  Moment,
  Notice,
  Reveal,
  Select,
  Table,
  TextInput,
  type Column,
} from '../../../shell/kit';
import { emptyGroup, GroupEditor, toDefinition, type GroupNode } from './builder';
import { segmentFields, type FieldCatalog } from '../api';
import { errorText } from '../../../shell/errors';
import { useConfirm } from '../../../shell/confirm';

export function SegmentsView({
  t,
  locale,
}: {
  t: IdentityTranslate;
  locale: IdentityLocale;
}) {
  const [root, setRoot] = useState<GroupNode>(emptyGroup);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [mode, setMode] = useState<'live' | 'scheduled'>('live');
  const [interval, setInterval] = useState(60);
  const [size, setSize] = useState<number | null>(null);
  const [segments, setSegments] = useState<readonly SegmentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<MembersPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /*
   * Справочник полей: группы, пояснения и допустимые значения. Приходит с
   * сервера, потому что объекты, уровни лояльности и марки живут в базе.
   * Конструктор работает и без него — тогда без группировки и с ручным вводом
   * значений, как было раньше.
   */
  const [catalog, setCatalog] = useState<FieldCatalog | null>(null);
  /** Сегмент, открытый на правку; `null` — конструктор заводит новый. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const confirm = useConfirm();

  useEffect(() => {
    void segmentFields()
      .then(setCatalog)
      .catch(() => setCatalog(null));
  }, []);

  const fail = (err: unknown): void => {
    setError(errorText(err).message);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listSegments();
      setSegments(result.segments);
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const preview = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const result = await previewSegment(toDefinition(root));
      setSize(result.size);
    } catch (err) {
      setSize(null);
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const save = async (): Promise<void> => {
    if (!code.trim() || !name.trim()) {
      setError(t('identity.common.required'));
      return;
    }
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      if (editingId) {
        await updateSegment(editingId, {
          name: name.trim(),
          definition: toDefinition(root),
          refreshMode: mode,
          ...(mode === 'scheduled' ? { intervalMinutes: interval } : {}),
        });
      } else {
        await createSegment({
          code: code.trim(),
          name: name.trim(),
          definition: toDefinition(root),
          refreshMode: mode,
          ...(mode === 'scheduled' ? { intervalMinutes: interval } : {}),
        });
      }
      setInfo(t('identity.segments.saved'));
      setCode('');
      setName('');
      setEditingId(null);
      setRoot(emptyGroup());
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (s: SegmentSummary): void => {
    const definition = s.definition as { root?: GroupNode } | null;
    setEditingId(s.id);
    setCode(s.code);
    setName(s.name);
    setMode(s.refreshMode);
    setRoot(definition?.root ?? emptyGroup());
    setSize(null);
    setInfo(null);
    setError(null);
  };

  const remove = async (s: SegmentSummary): Promise<void> => {
    const ok = await confirm({
      title: t('identity.segments.deleteTitle', { name: s.name }),
      body: t('identity.segments.deleteBody'),
      tone: 'danger',
      confirmLabel: t('identity.segments.delete'),
    });
    if (!ok) return;
    try {
      await archiveSegment(s.id);
      if (editingId === s.id) setEditingId(null);
      await load();
    } catch (err) {
      fail(err);
    }
  };

  const columns: ReadonlyArray<Column<SegmentSummary>> = [
    { key: 'name', header: t('identity.segments.name'), render: (s) => s.name },
    { key: 'code', header: t('identity.segments.code'), render: (s) => <code className="text-xs">{s.code}</code> },
    {
      key: 'mode',
      header: t('identity.segments.mode'),
      render: (s) =>
        s.refreshMode === 'live' ? t('identity.segments.modeLive') : t('identity.segments.modeScheduled'),
    },
    { key: 'size', header: t('identity.segments.lastSize'), render: (s) => s.lastSize ?? '—' },
    {
      key: 'built',
      header: t('identity.segments.asOf'),
      render: (s) => <Moment iso={s.lastBuiltAt} />,
    },
    {
      key: 'actions',
      header: '',
      render: (s) => (
        <div className="flex gap-2">
          <Button
            onClick={() =>
              void (async () => {
                try {
                  setMembers(await segmentMembers(s.id));
                } catch (err) {
                  fail(err);
                }
              })()
            }
          >
            {t('identity.segments.members')}
          </Button>
          {s.status === 'active' ? (
            <>
              <Button onClick={() => startEdit(s)}>{t('identity.segments.edit')}</Button>
              <Button tone="danger" onClick={() => void remove(s)}>
                {t('identity.segments.delete')}
              </Button>
            </>
          ) : (
            <span className="text-xs text-ink-faint">{t('identity.segments.archived')}</span>
          )}
          {/*
            Кнопка есть только у сегментов по расписанию, и это не случайность:
            «живой» состав считается запросом в момент обращения — пересобирать
            там нечего. Раньше на месте кнопки была пустота, и разница выглядела
            сбоем.
          */}
          {s.refreshMode === 'scheduled' ? (
            <Button
              onClick={() =>
                void (async () => {
                  try {
                    await refreshSegment(s.id);
                    await load();
                  } catch (err) {
                    fail(err);
                  }
                })()
              }
            >
              {t('identity.segments.refresh')}
            </Button>
          ) : (
            <span className="text-ink-faint text-xs">{t('identity.segments.liveNoRefresh')}</span>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Notice tone="info">{t('identity.segments.noCode')}</Notice>
      {error ? <Notice tone="error">{error}</Notice> : null}
      {info ? <Notice tone="ok">{info}</Notice> : null}

      <Card
        title={editingId ? t('identity.segments.editing', { name }) : t('identity.segments.title')}
        {...(editingId
          ? {
              actions: (
                <Button
                  onClick={() => {
                    setEditingId(null);
                    setCode('');
                    setName('');
                    setRoot(emptyGroup());
                  }}
                >
                  {t('identity.segments.cancelEdit')}
                </Button>
              ),
            }
          : {})}
      >
        <div className="mb-3 grid gap-3 md:grid-cols-4">
          <Field label={t('identity.segments.name')}>
            <TextInput value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t('identity.segments.code')}>
            <TextInput
              value={code}
              disabled={editingId !== null}
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
          <Field label={t('identity.segments.mode')}>
            <Select value={mode} onChange={(e) => setMode(e.target.value as 'live' | 'scheduled')}>
              <option value="live">{t('identity.segments.modeLive')}</option>
              <option value="scheduled">{t('identity.segments.modeScheduled')}</option>
            </Select>
          </Field>
          {mode === 'scheduled' ? (
            <Field label={t('identity.segments.interval')}>
              <TextInput
                type="number"
                value={interval}
                onChange={(e) => setInterval(Number(e.target.value))}
              />
            </Field>
          ) : null}
        </div>

        <GroupEditor node={root} t={t} catalog={catalog} locale={locale} onChange={setRoot} />

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button onClick={() => void preview()} disabled={busy}>
            {t('identity.segments.preview')}
          </Button>
          {size !== null ? (
            <span className="rounded bg-sunken px-3 py-1.5 text-sm font-medium text-ink">
              {t('identity.segments.size', { n: size })}
            </span>
          ) : null}
          <Button tone="primary" onClick={() => void save()} disabled={busy}>
            {t('identity.segments.save')}
          </Button>
        </div>
      </Card>

      <Card title={t('identity.segments.existing')}>
        <DataTable
          loading={loading}
          rows={segments}
          columns={columns}
          empty={t('identity.common.nothing')}
          rowKey={(s) => s.id}
        />
      </Card>

      {/*
        * Состав сегмента считается по нажатию и рисуется под таблицей — то есть
        * за пределами экрана, если сегментов много. Панель подтягивает себя в вид.
        */}
      {members ? (
        <Reveal when={members.asOf}>
        <Card title={t('identity.segments.members')}>
          <p className="mb-2 text-sm text-ink-muted">
            {t('identity.segments.asOf')}: <Moment iso={members.asOf} /> ·{' '}
            {members.mode === 'live'
              ? t('identity.segments.modeLive')
              : t('identity.segments.modeScheduled')}{' '}
            · {t('identity.segments.size', { n: members.customerIds.length })}
          </p>
          {/*
            Столбец идентификаторов заменён карточками: по ULID сегмент нельзя
            ни проверить («почему сюда попал вот этот?»), ни просто убедиться,
            что он собрал живых людей.
          */}
          <div className="max-h-80 overflow-y-auto">
            <Table>
              <thead className="text-ink-muted">
                <tr>
                  <th className="pb-2 text-left font-medium">
                    {t('identity.segments.memberName')}
                  </th>
                  <th className="pb-2 text-left font-medium">
                    {t('identity.segments.memberPhone')}
                  </th>
                  <th className="pb-2 text-right font-medium">
                    {t('identity.segments.memberVisits')}
                  </th>
                  <th className="pb-2 text-left font-medium">
                    {t('identity.segments.memberLastVisit')}
                  </th>
                  <th className="pb-2 text-left font-medium">
                    {t('identity.segments.memberTier')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {members.members.map((m) => (
                  <tr key={m.id} className="border-t border-line">
                    <td className="py-1 pr-3">
                      <div className="text-ink">
                        {m.displayName ?? t('identity.segments.memberNoName')}
                      </div>
                      <Id value={m.id} />
                    </td>
                    <td className="pr-3">{m.phone ?? '—'}</td>
                    <td className="pr-3 text-right tabular-nums">{m.visitCount}</td>
                    <td className="pr-3">
                      {m.lastVisitAt ? <Moment iso={m.lastVisitAt} /> : '—'}
                    </td>
                    <td className="pr-3">{m.loyaltyTier ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="mt-3">
            <Button onClick={() => setMembers(null)}>{t('identity.common.close')}</Button>
          </div>
        </Card>
        </Reveal>
      ) : null}
    </div>
  );
}
